import { Button, Icon } from "armstrong-react";
import { Col, Grid, Row } from "armstrong-react-fix";
import React from "react";
import _ from "underscore";

import { Avatar } from "../../../core/atoms/atoms";
import { DropdownButton } from "../../../core/atoms/atoms";
import { GoogleAnalyticsHelper } from "@travellocal/ui";
import { PriceCalculator } from "../currency/priceCalculator";
import { AlloyEditorComponent, EditorLevel } from "./../../../portal/components/controls/alloyEditorComponent";

interface State {
  error?: string;
  sendingMessage?: boolean;
  draftHtml?: string;
}

interface OwnProps {
  userType: UserType;
  draft: IState<string>;
  enquiry: IEnquiryOutput;
  disabled?: boolean;
  saveDraftMessage: (message: string) => void;
  sendMessage: () => Promise<void>;
  messageThread: IMessageThread;
}

export class NewMessageField extends React.Component<OwnProps, State>{
  private alloyEditor: AlloyEditorComponent;

  public constructor(props) {
    super(props);
    this.state = {
      sendingMessage: false,
    };
  }

  private saveDraft = () => this.props.saveDraftMessage(this.state.draftHtml);
  private saveDraftThrottled = _.throttle(this.saveDraft, 2500);

  private sendMessage = async () => {
    this.setState({ sendingMessage: true }, async () => {
      try {
        await this.props.saveDraftMessage(this.alloyEditor.getHtml());
        await this.props.sendMessage();
        this.alloyEditor.clearContent();
      }
      catch (e) {
        const err = e as IFetchApiFailure;
        this.setState({ error: err.validations ? err.validations.join(", ") : err.message });
      }

      this.setState({ sendingMessage: false });
    });

    const messageThread = this.props.messageThread.thread.data;
    const messagesFromCustomer = messageThread.filter(message => message.toCustomer === false);
    const numberOfMessagesFromLTO = messageThread.length - messagesFromCustomer.length;
    // check for 1 because initial enquiry is a message from customer, so first real message will be 2
    if (messagesFromCustomer.length === 1 && numberOfMessagesFromLTO > 0 && this.props.userType === UserType.Customer) {
      GoogleAnalyticsHelper.logEvent("Engaged", "First reply", "Messages tab", null, this.props.enquiry.reference);
    } else {
      GoogleAnalyticsHelper.logEvent("Send message", "Click", "Messages tab");
    }
  }

  public componentWillUnmount() {
    if (this.alloyEditor) {
      this.props.saveDraftMessage(this.alloyEditor.getHtml());
    }
  }

  private renderInsertActions = () => {
    return (
      <div className="message-insert-actions">
        <DropdownButton
          className="button-link"
          icomoon={Icon.Icomoon.calculator}
          label="Price Calculator"
          renderDropdown={() => {
            return (
              <PriceCalculator
                insertPrice={priceString => this.alloyEditor.insertText(priceString)}
              />
            );
          }}
        />
      </div>
    );
  }

  public render() {
    const { userType } = this.props;

    const isCustomer = userType === UserType.Customer;
    const placeholder = isCustomer
      ? "Enter your message here..."
      : "Enter your message to the customer here... \r\nIf you include any prices in this message, you MUST include TravelLocal's commission";

    const draftMessage = this.props.draft.status === "OK" ? this.props.draft.data || "" : "";

    return (
      <div className="new-message-field">
        {this.state.error &&
          <div className="validation-bar">
            <strong>Something went wrong:</strong> {this.state.error}
          </div>
        }
        <Grid>
          <Row>
            <Col width={70} className="rs-small-hide" />
            <Col>
              <h3>New message</h3>
              {!isCustomer && this.renderInsertActions()}
            </Col>
          </Row>
          <Row>
            <Col width={50} className="m-right-small rs-small-hide">
              <Grid className="avatar-container">
                <Row className="p-bottom-xxsmall">
                  <Col><Avatar size={Avatar.UISizes.normal} source={null} /></Col>
                </Row>
                <Row>
                  <Col className="author"><span>You</span></Col>
                </Row>
              </Grid>
            </Col>
            <Col>
              <AlloyEditorComponent ref={ae => this.alloyEditor = ae}
                                    imageUploadType="message"
                                    editorLevel={this.props.userType === UserType.LtoEmployee ? EditorLevel.minimalWithImage : EditorLevel.minimal}
                                    value={draftMessage}
                                    placeholder={placeholder}
                                    onChange={html => this.setState({ draftHtml: html }, this.saveDraftThrottled)}
                                    disabled={this.props.disabled ? this.props.disabled : undefined} />
            </Col>
          </Row>
        </Grid>

        <div className="send-message-row">
          <Button className="btn primary-button" disabled={draftMessage.length < 1} pending={this.state.sendingMessage} onClick={() => this.sendMessage()} leftIcon={Icon.Icomoon.envelop}>Send message</Button>
        </div>

        {this.props.userType === UserType.LtoEmployee &&
          <Grid>
            <Row className="p-top-small tip-area">
              <Col width={70} className="rs-small-hide" />
              <Col>
                <p className="m-top-xsmall">
                  <strong>Tips for writing messages</strong>
                </p>
                <p>
                  Make a new paragraph with <strong>Enter</strong>. Make a line break with <strong>Shift and Enter</strong> together. <br />
                  To create a link, highlight some words and click <Icon icon={Icon.Icomoon.link} />. <br />
                  To add an image to a message, click in an empty space and click the plus then the image icon that pops up. <br />
                  Please do not upload anything that contains personal or sensitive data.
                </p>
              </Col>
            </Row>
          </Grid>
        }
      </div>
    );
  }
}
